import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent } from "react";

import {
  createInviteCode,
  findUser,
  issueRecoveryCode,
  listInviteCodes,
  revokeInviteCode,
  setUserActive,
} from "../../api/admin";
import type { AdminUser, InviteCode, InviteCodeCreated, ModeratorRecoveryCode } from "../../api/admin";
import { errorText } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Field, FormError } from "../../components/Field";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { endOfDay, formatDate, formatDateTime } from "../../lib/format";
import styles from "./admin.module.css";

const t = nl.admin;
const INVITES_KEY = ["admin", "invite-codes"] as const;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? t.invites.copied : t.invites.copy}
    </Button>
  );
}

function NewInviteForm({ onCreated }: { onCreated: (invite: InviteCodeCreated) => void }) {
  const client = useQueryClient();
  const [label, setLabel] = useState("");
  const [expires, setExpires] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [labelError, setLabelError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: createInviteCode,
    onSuccess: (invite) => {
      setLabel("");
      setExpires("");
      setMaxUses("");
      void client.invalidateQueries({ queryKey: INVITES_KEY });
      onCreated(invite);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!label.trim()) {
      setLabelError(nl.auth.required);
      return;
    }
    setLabelError(null);
    create.mutate({
      label: label.trim(),
      expires_at: expires ? endOfDay(expires) : null,
      max_uses: maxUses ? Number(maxUses) : null,
    });
  }

  return (
    <form className={styles.card} onSubmit={submit} noValidate aria-labelledby="new-invite-title">
      <h2 id="new-invite-title" className={styles.sectionTitle}>
        {t.invites.newTitle}
      </h2>
      <FormError message={create.error ? errorText(create.error) : null} />
      <Field
        label={t.invites.label}
        placeholder={t.invites.labelPlaceholder}
        maxLength={100}
        value={label}
        error={labelError}
        onChange={(event) => {
          setLabel(event.target.value);
        }}
      />
      <div className={styles.twoColumns}>
        <Field
          label={t.invites.expires}
          type="date"
          help={t.invites.expiresHelp}
          value={expires}
          onChange={(event) => {
            setExpires(event.target.value);
          }}
        />
        <Field
          label={t.invites.maxUses}
          type="number"
          inputMode="numeric"
          min={1}
          max={10000}
          help={t.invites.maxUsesHelp}
          value={maxUses}
          onChange={(event) => {
            setMaxUses(event.target.value);
          }}
        />
      </div>
      <div>
        <Button type="submit" variant="primary" disabled={create.isPending}>
          {create.isPending ? nl.auth.working : t.invites.create}
        </Button>
      </div>
    </form>
  );
}

function InviteRow({ invite, onRevoke }: { invite: InviteCode; onRevoke: (invite: InviteCode) => void }) {
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.rowTitle}>{invite.label}</span>
        <span className={styles.rowMeta}>
          {t.invites.used(invite.uses, invite.max_uses ?? null)} ·{" "}
          {invite.expires_at ? t.invites.expiresOn(formatDate(invite.expires_at)) : t.invites.noExpiry}
        </span>
      </div>
      <span className={[styles.state, styles[invite.state]].join(" ")}>{t.invites.states[invite.state]}</span>
      {invite.state !== "revoked" && (
        <Button
          variant="ghost"
          onClick={() => {
            onRevoke(invite);
          }}
        >
          {t.invites.revoke}
        </Button>
      )}
    </li>
  );
}

function InviteList() {
  const client = useQueryClient();
  const toast = useToast();
  const invites = useQuery({ queryKey: INVITES_KEY, queryFn: listInviteCodes });
  const [revoking, setRevoking] = useState<InviteCode | null>(null);
  const revoke = useMutation({
    mutationFn: revokeInviteCode,
    onSuccess: () => {
      setRevoking(null);
      toast({ message: t.invites.revoked });
      void client.invalidateQueries({ queryKey: INVITES_KEY });
    },
  });

  let content;
  if (invites.isPending) {
    content = (
      <div className={styles.skeletons} aria-hidden="true">
        <Skeleton height={56} />
        <Skeleton height={56} />
      </div>
    );
  } else if (invites.isError) {
    content = (
      <EmptyState
        title={t.invites.loadError}
        action={<Button onClick={() => void invites.refetch()}>{nl.common.retry}</Button>}
      />
    );
  } else if (invites.data.length === 0) {
    content = <EmptyState title={t.invites.empty} />;
  } else {
    content = (
      <ul className={styles.list} aria-label={t.invites.listTitle}>
        {invites.data.map((invite) => (
          <InviteRow key={invite.id} invite={invite} onRevoke={setRevoking} />
        ))}
      </ul>
    );
  }

  return (
    <section aria-labelledby="invite-list-title">
      <h2 id="invite-list-title" className={styles.sectionTitle}>
        {t.invites.listTitle}
      </h2>
      {content}
      <Dialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
        title={t.invites.revokeTitle}
        description={revoking ? t.invites.revokeText(revoking.label) : undefined}
      >
        <FormError message={revoke.error ? errorText(revoke.error) : null} />
        <div className={styles.dialogActions}>
          <Button
            onClick={() => {
              setRevoking(null);
            }}
          >
            {t.invites.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={revoke.isPending}
            onClick={() => {
              if (revoking) revoke.mutate(revoking.id);
            }}
          >
            {t.invites.revokeConfirm}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

function UserLookup() {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [user, setUser] = useState<AdminUser | null>(null);
  const [recovery, setRecovery] = useState<ModeratorRecoveryCode | null>(null);
  const lookup = useMutation({ mutationFn: findUser, onSuccess: setUser });
  const issue = useMutation({ mutationFn: issueRecoveryCode, onSuccess: setRecovery });
  const status = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setUserActive(id, active),
    onSuccess: (updated, { active }) => {
      setUser(updated);
      toast({
        message: active ? t.users.activated_toast : t.users.deactivated_toast,
        onUndo: () => {
          status.mutate({ id: updated.id, active: !active });
        },
      });
    },
  });
  const error = lookup.error ?? issue.error ?? status.error;

  return (
    <section className={styles.card} aria-labelledby="user-lookup-title">
      <h2 id="user-lookup-title" className={styles.sectionTitle}>
        {t.users.title}
      </h2>
      <p className={styles.lead}>{t.users.lead}</p>
      <form
        className={styles.inline}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setUser(null);
          if (email.trim()) lookup.mutate(email.trim());
        }}
      >
        <Field
          className={styles.grow}
          label={t.users.email}
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={nl.auth.emailPlaceholder}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          trailing={
            <Button type="submit" variant="primary" disabled={lookup.isPending}>
              {t.users.find}
            </Button>
          }
        />
      </form>
      <FormError message={error ? errorText(error) : null} />
      {user && (
        <div className={styles.userCard} aria-live="polite">
          <div className={styles.rowMain}>
            <span className={styles.rowTitle}>{user.email}</span>
            <span className={styles.rowMeta}>
              {[user.initials, user.role === "moderator" ? t.users.moderator : null, t.users.created(formatDate(user.created_at))]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
          <span className={[styles.state, user.status === "active" ? styles.active : styles.revoked].join(" ")}>
            {user.status === "active" ? t.users.active : t.users.deactivated}
          </span>
          <div className={styles.userActions}>
            <Button
              disabled={issue.isPending}
              onClick={() => {
                issue.mutate(user.id);
              }}
            >
              {t.users.recoveryCode}
            </Button>
            <Button
              disabled={status.isPending}
              onClick={() => {
                status.mutate({ id: user.id, active: user.status !== "active" });
              }}
            >
              {user.status === "active" ? t.users.deactivate : t.users.activate}
            </Button>
          </div>
        </div>
      )}
      <Dialog
        open={recovery !== null}
        onOpenChange={(open) => {
          if (!open) setRecovery(null);
        }}
        title={t.users.recoveryTitle}
        description={recovery ? t.users.recoveryText(formatDateTime(recovery.expires_at)) : undefined}
      >
        {recovery && <SecretBox value={recovery.code} onClose={() => { setRecovery(null); }} />}
      </Dialog>
    </section>
  );
}

function SecretBox({ value, onClose }: { value: string; onClose: () => void }) {
  return (
    <>
      <p className={styles.secret}>{value}</p>
      <div className={styles.dialogActions}>
        <CopyButton text={value} />
        <Button variant="primary" onClick={onClose}>
          {t.invites.close}
        </Button>
      </div>
    </>
  );
}

export function InviteCodesPage() {
  const [created, setCreated] = useState<InviteCodeCreated | null>(null);
  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{nl.screens.inviteCodes}</h1>
      <p className={styles.lead}>{t.invites.lead}</p>
      <NewInviteForm onCreated={setCreated} />
      <InviteList />
      <UserLookup />
      <Dialog
        open={created !== null}
        onOpenChange={(open) => {
          if (!open) setCreated(null);
        }}
        title={t.invites.createdTitle}
        description={t.invites.createdText}
      >
        {created && <SecretBox value={created.code} onClose={() => { setCreated(null); }} />}
      </Dialog>
    </div>
  );
}
